import { MasteryLevel } from "../types/enums";
import { GRADE_ORDER } from "../types/standards";
import type {
  ClusterNode,
  DomainNode,
  Grade,
  LessonInfo,
  MasteryCategory,
  MasteryCellInfo,
  StudentMastery,
  StudentMasteryCell,
} from "../types/standards";

export const MASTERY_CATEGORIES: Record<MasteryLevel, MasteryCategory> = {
  [MasteryLevel.NOT_STARTED]: {
    key: MasteryLevel.NOT_STARTED,
    label: "N/A",
    dot: "#C9CDD3",
    pill: "#F0F1F3",
    pillInk: "#9AA0A6",
  },
  [MasteryLevel.APPROACHING]: {
    key: MasteryLevel.APPROACHING,
    label: "Approaching",
    dot: "#FFA100",
    pill: "#FFEFC9",
    pillInk: "#7A4B00",
  },
  [MasteryLevel.PROFICIENT]: {
    key: MasteryLevel.PROFICIENT,
    label: "Proficient",
    dot: "#1C9C6D",
    pill: "#D7F1E4",
    pillInk: "#0F5132",
  },
  [MasteryLevel.MASTERED]: {
    key: MasteryLevel.MASTERED,
    label: "Mastered",
    dot: "#5AA2F0",
    pill: "#E1EEFC",
    pillInk: "#2B6CB0",
  },
};

export function masteryLevel(mean: number, started: boolean): MasteryLevel {
  if (!started) return MasteryLevel.NOT_STARTED;
  if (mean < 0.5) return MasteryLevel.APPROACHING;
  if (mean < 0.8) return MasteryLevel.PROFICIENT;
  return MasteryLevel.MASTERED;
}

export function hasDirectMastery(
  cell: Pick<StudentMasteryCell, "started" | "evidence"> | undefined,
): boolean {
  return Boolean(cell?.started && cell.evidence > 0);
}

export function gradeLabel(grade: Grade): string {
  return grade === "HS" ? "High School" : `Grade ${grade}`;
}

const GRADE_VALUES = new Set<Grade>(GRADE_ORDER);

// A domain/cluster's "instruction bucket": its HS category (e.g. "Algebra")
// when it has one, its real grade otherwise. HS domains all share grade
// "HS", so category is what actually distinguishes them for filtering.
export function bucketOf(node: {
  grade?: Grade;
  category?: string;
}): string | undefined {
  return node.category ?? node.grade;
}

// Label for a bucket value from bucketOf/instructionLevel — a real grade
// ("K"/"1".."8"/"HS") or an HS category name, which isn't display-ready on
// its own (e.g. "Algebra"), so it gets an "HS " prefix for parity with how
// real grades read ("Grade 3").
export function bucketLabel(bucket: string): string {
  return GRADE_VALUES.has(bucket as Grade)
    ? gradeLabel(bucket as Grade)
    : `HS ${bucket}`;
}

export function flattenCodes(map: DomainNode[]): string[] {
  const codes: string[] = [];
  map.forEach((d) =>
    d.clusters.forEach((c) => c.standards.forEach((s) => codes.push(s.id))),
  );
  return codes;
}

// Standard code -> its instruction bucket (see bucketOf).
export function codeGradeMap(map: DomainNode[]): Map<string, string> {
  const out = new Map<string, string>();
  map.forEach((d) => {
    const bucket = bucketOf(d);
    if (bucket == null) return;
    d.clusters.forEach((c) =>
      c.standards.forEach((s) => out.set(s.id, bucket)),
    );
  });
  return out;
}

// Instruction bucket with the most directly evidenced started standards; falls back to
// the student's enrolled grade (also a valid bucket value: "K"/"1".."8"/"HS").
export function instructionLevel(
  student: StudentMastery,
  codeGrade: Map<string, string>,
): string | undefined {
  const counts = new Map<string, number>();
  for (const code in student.cells) {
    if (!hasDirectMastery(student.cells[code])) continue;
    const bucket = codeGrade.get(code);
    if (bucket === undefined) continue;
    counts.set(bucket, (counts.get(bucket) ?? 0) + 1);
  }
  let best: string | undefined = student.grade;
  let bestCount = -1;
  counts.forEach((count, bucket) => {
    if (count > bestCount) {
      bestCount = count;
      best = bucket;
    }
  });
  return best;
}

// Combines domains sharing the same code (K-8, e.g. all "OA" domains) into
// one domain per code, tagging each cluster with its origin grade — a
// merged domain spans grades, so grade moves down to the cluster level.
// HS domains (hyphenated codes, e.g. "G-CO") pass through unchanged.
// domainOrder, when given, fixes the K-8 order by code instead of by name; a
// code it does not list goes after the listed ones, by name.
export function mergeDomainsByCode(
  map: DomainNode[],
  domainOrder?: string[],
): DomainNode[] {
  const merged = new Map<string, DomainNode>();
  const order: string[] = [];
  map.forEach((d) => {
    if (d.code.includes("-")) {
      merged.set(d.id, d);
      order.push(d.id);
      return;
    }
    const taggedClusters: ClusterNode[] = d.clusters.map((c) => ({
      ...c,
      grade: d.grade,
    }));
    const existing = merged.get(d.code);
    if (existing) {
      existing.clusters = [...existing.clusters, ...taggedClusters];
    } else {
      merged.set(d.code, {
        id: d.code,
        code: d.code,
        name: d.name,
        clusters: taggedClusters,
      });
      order.push(d.code);
    }
  });
  // K-8 first, HS last, alphabetical within each group.
  return order
    .map((key) => merged.get(key)!)
    .sort((a, b) => {
      const aIsHS = a.category != null ? 1 : 0;
      const bIsHS = b.category != null ? 1 : 0;
      if (aIsHS !== bIsHS) return aIsHS - bIsHS;
      const rank = (code: string) => {
        const index = domainOrder?.indexOf(code) ?? -1;
        return index === -1 ? Infinity : index;
      };
      const byRank = rank(a.code) - rank(b.code);
      return Number.isNaN(byRank) || byRank === 0
        ? a.name.localeCompare(b.name)
        : byRank;
    });
}

function sumStarted(
  student: StudentMastery,
  codes: string[],
): { sum: number; n: number } {
  let sum = 0;
  let n = 0;
  codes.forEach((code) => {
    const cell = student.cells[code];
    if (hasDirectMastery(cell)) {
      sum += cell.mastery;
      n++;
    }
  });
  return { sum, n };
}

export function overallMastery(
  student: StudentMastery,
  codes: string[],
): number {
  const { sum, n } = sumStarted(student, codes);
  return n ? sum / n : 0;
}

export type LessonGroup = {
  /** Stable within one cell — for React keys and expansion state. */
  key: string;
  lesson_id?: number;
  lesson_title: string;
  got: number;
  total: number;
  attempts: LessonInfo[];
};

/**
 * Fold a cell's per-attempt entries into one group per lesson, oldest first.
 *
 * Grouped by lesson_id, not by title: two distinct lessons can carry the same
 * name, and merging them would report one card with their scores added up. The
 * frozen demo capture predates that field, so those entries fall back to the
 * title — the older behaviour, rather than collapsing a whole cell into one
 * group under an undefined id.
 */
export function groupLessons(lessons: LessonInfo[]): LessonGroup[] {
  const order: string[] = [];
  const byKey = new Map<string, LessonGroup>();
  lessons.forEach((l) => {
    const key =
      l.lesson_id != null ? `id:${l.lesson_id}` : `title:${l.lesson_title}`;
    let group = byKey.get(key);
    if (!group) {
      group = {
        key,
        lesson_id: l.lesson_id,
        lesson_title: l.lesson_title,
        got: 0,
        total: 0,
        attempts: [],
      };
      byKey.set(key, group);
      order.push(key);
    }
    group.got += l.got;
    group.total += l.total;
    group.attempts.push(l);
  });
  return order.map((key) => byKey.get(key)!);
}

export function aggregateCells(
  student: StudentMastery,
  codes: string[],
): MasteryCellInfo {
  const { sum, n } = sumStarted(student, codes);
  return {
    mastery: n ? sum / n : 0,
    started: n > 0,
    evidence: n,
    code: null,
    isAggregate: true,
    total: codes.length,
    startedCount: n,
  };
}
